import type { AppLanguage } from '../i18n/types';
import type { GeoCoordinates } from './assetGeo';
import { assetListSortKey, sortAssetRecordsAlphabetically } from './assetListNormalize';
import { assetDistanceKm } from './homeAssetRanking';
import { resolveListingHourlyDailyPrices } from './assetBookingPricingOptions';

export type AssetBrowseListSortMode = 'name' | 'distance' | 'priceAsc' | 'priceDesc';

export type AssetListingPriceUnit = 'hour' | 'day' | 'month';

export type AssetListingSortPrice = {
  amount: number;
  unit: AssetListingPriceUnit;
};

const PRICE_UNIT_TIER: Record<AssetListingPriceUnit, number> = { hour: 0, day: 1, month: 2 };

export function isAssetBrowseListSortMode(value: unknown): value is AssetBrowseListSortMode {
  return value === 'name' || value === 'distance' || value === 'priceAsc' || value === 'priceDesc';
}

/** Hourly rate first, then daily, then monthly — rates in different units are never compared. */
export function assetListingSortPrice(asset: Record<string, unknown>): AssetListingSortPrice | null {
  const { hourly, daily, monthly } = resolveListingHourlyDailyPrices(asset);
  if (hourly !== null && Number.isFinite(hourly) && hourly >= 0) {
    return { amount: hourly, unit: 'hour' };
  }
  if (daily !== null && Number.isFinite(daily) && daily >= 0) {
    return { amount: daily, unit: 'day' };
  }
  if (monthly !== null && Number.isFinite(monthly) && monthly >= 0) {
    return { amount: monthly, unit: 'month' };
  }
  return null;
}

function sortByPrice(
  records: Record<string, unknown>[],
  descending: boolean,
  language: AppLanguage,
): Record<string, unknown>[] {
  const locale = language === 'he' ? 'he' : undefined;
  return [...records].sort((a, b) => {
    const pa = assetListingSortPrice(a);
    const pb = assetListingSortPrice(b);
    if (pa && !pb) {
      return -1;
    }
    if (!pa && pb) {
      return 1;
    }
    if (pa && pb) {
      const tier = PRICE_UNIT_TIER[pa.unit] - PRICE_UNIT_TIER[pb.unit];
      if (tier !== 0) {
        return tier;
      }
      if (pa.amount !== pb.amount) {
        return descending ? pb.amount - pa.amount : pa.amount - pb.amount;
      }
    }
    return assetListSortKey(a, language).localeCompare(assetListSortKey(b, language), locale, {
      sensitivity: 'base',
      numeric: true,
    });
  });
}

export function sortAssetBrowseListRecords(
  records: Record<string, unknown>[],
  mode: AssetBrowseListSortMode,
  options?: {
    viewer?: GeoCoordinates | null;
    language?: AppLanguage;
  },
): Record<string, unknown>[] {
  const language = options?.language ?? 'en';
  const viewer = options?.viewer ?? null;

  if (mode === 'priceAsc' || mode === 'priceDesc') {
    return sortByPrice(records, mode === 'priceDesc', language);
  }

  if (mode === 'name' || !viewer) {
    return sortAssetRecordsAlphabetically(records, language);
  }

  const locale = language === 'he' ? 'he' : undefined;
  return [...records].sort((a, b) => {
    const da = assetDistanceKm(a, viewer);
    const db = assetDistanceKm(b, viewer);
    if (da === null && db === null) {
      return assetListSortKey(a, language).localeCompare(assetListSortKey(b, language), locale, {
        sensitivity: 'base',
        numeric: true,
      });
    }
    if (da === null) {
      return 1;
    }
    if (db === null) {
      return -1;
    }
    if (da !== db) {
      return da - db;
    }
    return assetListSortKey(a, language).localeCompare(assetListSortKey(b, language), locale, {
      sensitivity: 'base',
      numeric: true,
    });
  });
}
